import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { productsAPI, inventoryAPI, sucursalesAPI } from '../services/api';
import ProductTable from '../components/ProductTable';
import ProductForm from '../components/ProductForm';
import StockAdjustmentModal from '../components/StockAdjustmentModal';
import ActionModal from '../components/ActionModal';
import LotesModal from '../components/LotesModal';
import Toast from '../components/Toast';
import { PrecioEspecialModal } from '../components/SucursalPreciosView';
import ProductSearchModal from '../components/ProductSearchModal';
import QuickEditModal from '../components/QuickEditModal';
import { useSucursal, isAdmin, isEncargada, isCajero, isBotica, isJuguteria, getTipoNegocio } from '../hooks/useSucursal';
// 📱 Vista móvil optimizada
import ProductsMobile from '../components/ProductsMobile';
import './ProductsPage.css';


// Modal Interno para Stock
const InternalModal = ({ children, title, onClose }) => (
    <div className="product-modal-overlay" onClick={onClose}>
        <div className="product-modal-content" onClick={e => e.stopPropagation()}>
            <div className="product-modal-header">
                <h2>{title}</h2>
                <button className="close-btn" onClick={onClose}>&times;</button>
            </div>
            {children}
        </div>
    </div>
);

const ProductsPage = () => {
    // ==================== ESTADOS ====================
    const { sucursalId: currentSucursalId, sucursalNombre: currentSucursalNombre } = useSucursal();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Estados Modales
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showStockModal, setShowStockModal] = useState(false);
    const [stockProduct, setStockProduct] = useState(null);
    const [showLotesModal, setShowLotesModal] = useState(false);
    const [lotesProduct, setLotesProduct] = useState(null);
    const [alertasLotes, setAlertasLotes] = useState(null);

    // Estados Precio Especial Modal
    const [showPrecioModal, setShowPrecioModal] = useState(false);
    const [precioModalProduct, setPrecioModalProduct] = useState(null);

    // Estados ActionModal
    const [actionProduct, setActionProduct] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);

    // Notificaciones Toast
    const [toast, setToast] = useState(null); // { message: '', type: 'success'|'error' }

    // Filtros
    const [filter, setFilter] = useState('active');
    const [searchTerm, setSearchTerm] = useState('');
    const [stockFilter, setStockFilter] = useState('all');
    const [trashedCount, setTrashedCount] = useState(0);

    // Filtros de columna
    const [sortField, setSortField] = useState('');       // campo activo de orden
    const [sortDir, setSortDir] = useState('asc');        // 'asc' | 'desc'
    const [categoryFilter, setCategoryFilter] = useState('');
    const [unitFilter, setUnitFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // UI y Stats
    const [isSaving, setIsSaving] = useState(false);
    const [stats, setStats] = useState(null);

    // Modales nuevos
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showQuickEdit, setShowQuickEdit] = useState(false);

    // Estado importación Excel
    const [showImportModal, setShowImportModal] = useState(false);
    const [importRows, setImportRows] = useState([]);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null); // resumen final


    // Detectar móvil
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Selector de sucursal (exclusivo para el Dueño/Admin)
    const [sucursales, setSucursales] = useState([]);
    const [sucursalActiva, setSucursalActiva] = useState(null);

    // Sucursal efectiva:
    // - Para Dueño/Admin: usa sucursalActiva (o null para catálogo global).
    // - Para Encargada/Cajera: siempre usa currentSucursalId de su sesión activa.
    const effectiveSucursal = isAdmin() ? sucursalActiva : currentSucursalId;

    // ==================== EFECTOS ====================

    // Carga de productos y stats cuando cambian filtros o la sucursal efectiva
    useEffect(() => {
        loadProducts();
        loadStats();
        if (isBotica()) {
            loadAlertasLotes();
        }
    }, [filter, effectiveSucursal]);

    // Cargar lista de sucursales activas (para selector del Admin)
    useEffect(() => {
        if (isAdmin()) {
            sucursalesAPI.getAll()
                .then(res => setSucursales((res.data?.data?.sucursales || []).filter(s => s.activo)))
                .catch(() => setSucursales([]));
        }
    }, []);

    // Cargar contador de papelera
    useEffect(() => {
        const loadTrashedCount = async () => {
            try {
                const response = await productsAPI.getAll('inactive', effectiveSucursal);
                setTrashedCount(response.data?.data?.products?.length || 0);
            } catch (error) { console.error(error); }
        };
        if (filter === 'active') loadTrashedCount();
    }, [filter, products, effectiveSucursal]);

    // Ocultar toast automáticamente
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Detectar cambio de tamaño
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Atajo de teclado F2 → Edición Rápida
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'F2' && !isModalOpen && !showStockModal && !showLotesModal) {
                e.preventDefault();
                setShowQuickEdit(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isModalOpen, showStockModal, showLotesModal]);

    // ==================== CARGA DE DATOS ====================

    const loadProducts = async () => {
        setIsLoading(true);
        try {
            const response = await productsAPI.getAll(filter, effectiveSucursal);
            setProducts(response.data?.data?.products || []);
        } catch (error) {
            console.error("Error cargando productos:", error);
            setToast({ message: "Error al cargar productos", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await productsAPI.getStats(effectiveSucursal);
            setStats(response.data?.data || null);
        } catch (error) { console.error(error); }
    };

    const loadAlertasLotes = async () => {
        if (!isBotica()) return;
        try {
            const res = await inventoryAPI.getAlertasLotes({
                sucursal_id: effectiveSucursal,
                dias: 90
            });
            setAlertasLotes(res.data?.data || null);
        } catch (error) {
            console.error('Error cargando alertas de lotes:', error);
        }
    };

    // Categorías únicas extraídas de los productos cargados
    const uniqueCategories = useMemo(() =>
        [...new Set(products.map(p => p.category).filter(Boolean))].sort()
    , [products]);

    // Unidades únicas
    const uniqueUnits = useMemo(() =>
        [...new Set(products.map(p => p.unit).filter(Boolean))].sort()
    , [products]);

    // Filtrado + Ordenamiento
    const filteredProducts = useMemo(() => {
        let result = products;

        // Búsqueda de texto
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(product =>
                product.name.toLowerCase().includes(lowerTerm) ||
                product.code.toLowerCase().includes(lowerTerm) ||
                product.category?.toLowerCase().includes(lowerTerm)
            );
        }

        // Filtro de stock bajo / agotado / alertas
        if (stockFilter === 'low') {
            result = result.filter(p => p.stock <= p.min_stock && p.stock > 0);
        } else if (stockFilter === 'out') {
            result = result.filter(p => p.stock <= 0);
        } else if (stockFilter === 'vencidos' && alertasLotes?.alertas) {
            const ids = new Set(alertasLotes.alertas.filter(a => a.estado_vencimiento === 'VENCIDO').map(a => a.producto_id));
            result = result.filter(p => ids.has(p.id));
        } else if (stockFilter === 'criticos' && alertasLotes?.alertas) {
            const ids = new Set(alertasLotes.alertas.filter(a => a.estado_vencimiento === 'CRITICO').map(a => a.producto_id));
            result = result.filter(p => ids.has(p.id));
        } else if (stockFilter === 'por_vencer' && alertasLotes?.alertas) {
            const ids = new Set(alertasLotes.alertas.filter(a => a.estado_vencimiento === 'POR_VENCER').map(a => a.producto_id));
            result = result.filter(p => ids.has(p.id));
        }

        // Filtro por categoría
        if (categoryFilter) {
            result = result.filter(p => p.category === categoryFilter);
        }

        // Filtro por unidad
        if (unitFilter) {
            result = result.filter(p => (p.unit || 'unidad') === unitFilter);
        }

        // Filtro por estado
        if (statusFilter === 'active') {
            result = result.filter(p => p.is_active);
        } else if (statusFilter === 'inactive') {
            result = result.filter(p => !p.is_active);
        }

        // Ordenamiento
        if (sortField) {
            result = [...result].sort((a, b) => {
                let valA = a[sortField];
                let valB = b[sortField];

                if (sortField === 'sale_price' || sortField === 'stock') {
                    valA = parseFloat(valA) || 0;
                    valB = parseFloat(valB) || 0;
                } else if (sortField === 'is_active') {
                    valA = valA ? 1 : 0;
                    valB = valB ? 1 : 0;
                } else {
                    valA = (valA || '').toString().toLowerCase();
                    valB = (valB || '').toString().toLowerCase();
                }

                if (valA < valB) return sortDir === 'asc' ? -1 : 1;
                if (valA > valB) return sortDir === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [searchTerm, products, stockFilter, categoryFilter, unitFilter, statusFilter, sortField, sortDir, alertasLotes]);

    // ==================== MANEJO DE ACCIONES ====================

    // Helper para mostrar Toast rápido
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleOpenActionModal = (product) => {
        setActionProduct(product);
        setShowActionModal(true);
    };

    const handleCreate = () => {
        setSelectedProduct(null);
        setIsModalOpen(true);
    };

    const handleEdit = (product) => {
        setShowActionModal(false);
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    const handleAdjustStock = (product) => {
        setShowActionModal(false);
        setStockProduct(product);
        setShowStockModal(true);
    };

    const handleOpenLotes = (product) => {
        setShowActionModal(false);
        setLotesProduct(product);
        setShowLotesModal(true);
    };

    // --- ACCIONES CON TOAST ---

    const handleDelete = async (productId) => {
        setShowActionModal(false);
        if (window.confirm('¿Mover a la papelera?')) {
            setIsLoading(true);
            try {
                await productsAPI.delete(productId);
                showToast("🗑️ Producto movido a la papelera", "success");
                loadProducts();
                loadStats();
            } catch (error) {
                showToast("❌ Error al eliminar", "error");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleRestore = async (productId) => {
        setShowActionModal(false);
        if (window.confirm('¿Restaurar producto?')) {
            setIsLoading(true);
            try {
                await productsAPI.restore(productId);
                showToast("♻️ Producto restaurado", "success");
                loadProducts();
                loadStats();
            } catch (error) {
                showToast("❌ Error al restaurar", "error");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handlePermanentDelete = async (productId) => {
        setShowActionModal(false);
        if (window.confirm('⚠️ IRREVERSIBLE: ¿Eliminar permanentemente?')) {
            setIsLoading(true);
            try {
                await productsAPI.permanentDelete(productId);
                showToast("🚫 Producto eliminado permanentemente", "success");
                loadProducts();
                loadStats();
            } catch (error) {
                const msg = error.response?.data?.message || "❌ Error al eliminar";
                showToast(msg, "error");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleFormSubmit = async (productData) => {
        setIsSaving(true);
        try {
            const payload = { ...productData, sucursal_id: effectiveSucursal };
            if (selectedProduct) {
                await productsAPI.update(selectedProduct.id, payload);
                showToast("✅ Producto actualizado correctamente", "success");
            } else {
                await productsAPI.create(payload);
                showToast("✅ Producto creado correctamente", "success");
            }
            setIsModalOpen(false);
            loadProducts();
            loadStats();
        } catch (error) {
            showToast(error.response?.data?.message || "❌ Error al guardar", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleStockSubmit = async (data) => {
        try {
            await inventoryAPI.createMovement({
                product_id: stockProduct.id,
                movement_type: data.type,
                quantity: data.quantity,
                reason: data.reason,
                notes: data.notes || null,
                sucursal_id: effectiveSucursal
            });
            showToast(`📊 Stock actualizado: ${stockProduct.name}`, "success");
            setShowStockModal(false);
            loadProducts();
            loadStats();
        } catch (error) {
            console.error("Error ajustando stock:", error);
            showToast(error.response?.data?.message || "❌ Error al ajustar stock", "error");
        }
    };

    const exportToExcel = async () => {
        const ExcelJS = (await import('exceljs')).default;
        const tipoNegocio = getTipoNegocio(); // 'juguteria' | 'botica' | 'tienda_general'

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Productos');

        // ── Columnas BASE (comunes a todos los negocios) ──────────────────────
        const COLS_BASE = [
            { header: 'Código',       key: 'code',      width: 14, group: 'general' },
            { header: 'Nombre',       key: 'name',      width: 34, group: 'general' },
            { header: 'Categoría',    key: 'category',  width: 20, group: 'general' },
            { header: 'Unidad',       key: 'unit',      width: 12, group: 'general' },
            { header: 'Estado',       key: 'estado',    width: 12, group: 'general' },
        ];

        // ── Columnas exclusivas JUGUTERÍA / ARMABLES ─────────────────────────
        const COLS_JUGUTERIA = [
            { header: 'N° Piezas',        key: 'numero_piezas',    width: 12, group: 'especial' },
            { header: 'Nivel Dificultad', key: 'nivel_dificultad', width: 16, group: 'especial' },
            { header: 'Material',         key: 'material',         width: 22, group: 'especial' },
        ];

        // ── Columnas exclusivas BOTICA / FARMACIA ────────────────────────────
        const COLS_BOTICA = [
            { header: 'Presentación',       key: 'presentacion',      width: 18, group: 'especial' },
            { header: 'Laboratorio',        key: 'laboratorio',       width: 22, group: 'especial' },
            { header: 'Nombre Comercial',   key: 'nombre_comercial',  width: 22, group: 'especial' },
            { header: 'Concentración',      key: 'concentracion',     width: 16, group: 'especial' },
            { header: 'Principio Activo',   key: 'principio_activo',  width: 22, group: 'especial' },
            { header: 'Forma Farmacéutica', key: 'forma_farmaceutica',width: 20, group: 'especial' },
            { header: 'Titular',            key: 'titular',           width: 20, group: 'especial' },
            { header: 'Código de Barras',   key: 'codigo_barras',     width: 18, group: 'especial' },
        ];

        // ── Columnas comunes al FINAL (inventario + precios + obs) ──────────
        const COLS_FINAL = [
            { header: 'Stock Actual',  key: 'stock',          width: 13, group: 'inventario' },
            { header: 'Stock Mínimo',  key: 'min_stock',      width: 13, group: 'inventario' },
            { header: 'Precio Compra', key: 'purchase_price', width: 15, group: 'precios'    },
            { header: 'Precio Venta',  key: 'sale_price',     width: 15, group: 'precios'    },
            { header: 'Ganancia',      key: 'ganancia',       width: 14, group: 'precios'    },
            { header: 'Observaciones', key: 'description',    width: 40, group: 'obs'        },
        ];

        // ── Construir lista final según módulo ───────────────────────────────
        let COLS_ESPECIALES = [];
        if (tipoNegocio === 'juguteria') COLS_ESPECIALES = COLS_JUGUTERIA;
        else if (tipoNegocio === 'botica') COLS_ESPECIALES = COLS_BOTICA;
        // tienda_general → sin campos especiales

        const COLS = [...COLS_BASE, ...COLS_ESPECIALES, ...COLS_FINAL];

        // Anchos
        COLS.forEach((col, i) => { sheet.getColumn(i + 1).width = col.width; });

        // ── Título ───────────────────────────────────────────────────────────
        const totalCols = COLS.length;
        // Para > 26 columnas usar AA, AB… pero aquí no llegamos a eso con ningún módulo
        const lastColLetter = totalCols <= 26
            ? String.fromCharCode(64 + totalCols)
            : 'A' + String.fromCharCode(64 + totalCols - 26);

        const moduloLabel = tipoNegocio === 'juguteria' ? '🧩 Jugutería / Armables'
                          : tipoNegocio === 'botica'    ? '💊 Botica / Farmacia'
                          : '🏪 Tienda General';

        sheet.mergeCells(`A1:${lastColLetter}1`);
        const titleCell = sheet.getCell('A1');
        titleCell.value = `GESTIÓN DE PRODUCTOS — ${moduloLabel}  ·  ${new Date().toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })}`;
        titleCell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getRow(1).height = 28;
        sheet.addRow([]);

        // ── Encabezados con colores por grupo ────────────────────────────────
        const groupColors = {
            general:    'FF2563EB', // azul
            especial:   tipoNegocio === 'juguteria' ? 'FF7C3AED'   // morado
                      : tipoNegocio === 'botica'    ? 'FF065F46'   // verde oscuro
                      : 'FF374151',
            inventario: 'FF374151', // gris
            precios:    'FFB45309', // ámbar
            obs:        'FF1E3A5F', // azul oscuro
        };

        const headerRow = sheet.addRow(COLS.map(c => c.header));
        headerRow.height = 22;
        headerRow.eachCell((cell, colNum) => {
            const group = COLS[colNum - 1]?.group || 'general';
            cell.font      = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: groupColors[group] } };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border    = { bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } } };
        });

        // ── Índices dinámicos (dependen del número de columnas especiales) ───
        const baseCount    = COLS_BASE.length;             // 5
        const especCount   = COLS_ESPECIALES.length;       // 3 (juguteria) | 8 (botica) | 0
        const stockCol     = baseCount + especCount + 1;   // columna "Stock Actual"
        const compraCol    = baseCount + especCount + 3;   // "Precio Compra"
        const ventaCol     = baseCount + especCount + 4;   // "Precio Venta"
        const gananciaCol  = baseCount + especCount + 5;   // "Ganancia"
        const estadoCol    = 5;                            // siempre col 5

        // ── Filas de datos ────────────────────────────────────────────────────
        filteredProducts.forEach((p, index) => {
            const stock    = parseFloat(p.stock)          || 0;
            const compra   = parseFloat(p.purchase_price) || 0;
            const venta    = parseFloat(p.sale_price)     || 0;
            const ganancia = venta - compra;

            // Valores base
            const baseVals = [
                p.code     || '',
                p.name     || '',
                p.category || '',
                p.unit     || 'unidad',
                p.is_active ? 'Activo' : 'Inactivo',
            ];

            // Valores especiales según módulo
            let especVals = [];
            if (tipoNegocio === 'juguteria') {
                especVals = [
                    p.numero_piezas    != null ? p.numero_piezas : '',
                    p.nivel_dificultad || '',
                    p.material         || '',
                ];
            } else if (tipoNegocio === 'botica') {
                especVals = [
                    p.presentacion       || '',
                    p.laboratorio        || '',
                    p.nombre_comercial   || '',
                    p.concentracion      || '',
                    p.principio_activo   || '',
                    p.forma_farmaceutica || '',
                    p.titular            || '',
                    p.codigo_barras      || '',
                ];
            }

            // Valores finales
            const finalVals = [
                stock,
                p.min_stock || 0,
                compra,
                venta,
                ganancia,
                p.description || '',
            ];

            const row = sheet.addRow([...baseVals, ...especVals, ...finalVals]);

            const bgColor = index % 2 === 0 ? 'FFFFFFFF' : 'FFF1F5F9';
            row.eachCell((cell, colNumber) => {
                cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                cell.font      = { size: 10 };
                cell.alignment = { vertical: 'middle' };

                // Precios con formato moneda S/.
                if ([compraCol, ventaCol, gananciaCol].includes(colNumber)) {
                    cell.numFmt    = '"S/. "#,##0.00';
                    cell.alignment = { horizontal: 'right', vertical: 'middle' };
                }
                // Ganancia en color
                if (colNumber === gananciaCol) {
                    if (ganancia > 0) cell.font = { size: 10, color: { argb: 'FF059669' }, bold: true };
                    if (ganancia < 0) cell.font = { size: 10, color: { argb: 'FFDC2626' }, bold: true };
                }
                // Stock en color
                if (colNumber === stockCol) {
                    if (stock <= 0)                       cell.font = { size: 10, bold: true, color: { argb: 'FFDC2626' } };
                    else if (stock <= (p.min_stock || 0)) cell.font = { size: 10, bold: true, color: { argb: 'FFD97706' } };
                }
                // Estado en color
                if (colNumber === estadoCol) {
                    cell.font = { size: 10, bold: true, color: { argb: p.is_active ? 'FF059669' : 'FF94A3B8' } };
                }
            });
            row.height = 18;
        });

        // ── Fila de totales ────────────────────────────────────────────────
        const totalRowVals = Array(COLS.length).fill('');
        totalRowVals[1] = `TOTAL: ${filteredProducts.length} productos`;
        totalRowVals[stockCol - 1] = filteredProducts.reduce((s, p) => s + (parseFloat(p.stock) || 0), 0);
        const totalRow = sheet.addRow(totalRowVals);
        totalRow.getCell(2).font          = { bold: true, size: 10, color: { argb: 'FF1E3A5F' } };
        totalRow.getCell(stockCol).font   = { bold: true, size: 10 };
        totalRow.getCell(stockCol).numFmt = '#,##0';

        // ── Descargar ──────────────────────────────────────────────────────
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url  = window.URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `productos_${tipoNegocio}_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        showToast(`📥 Excel exportado — ${moduloLabel}`, 'success');
    };

    // Plantilla de Excel según el módulo del negocio
    const downloadTemplate = async () => {
        const ExcelJS = (await import('exceljs')).default;
        const tipoNegocio = getTipoNegocio();
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Plantilla Productos');

        const COLS_BASE = [
            { header: 'Código',       key: 'code',      width: 14 },
            { header: 'Nombre',       key: 'name',      width: 34 },
            { header: 'Categoría',    key: 'category',  width: 20 },
            { header: 'Unidad',       key: 'unit',      width: 12 },
        ];

        const COLS_JUGUTERIA = [
            { header: 'N° Piezas',        key: 'numero_piezas',    width: 12 },
            { header: 'Nivel Dificultad', key: 'nivel_dificultad', width: 16 },
            { header: 'Material',         key: 'material',         width: 20 },
        ];

        const COLS_BOTICA = [
            { header: 'Presentación',       key: 'presentacion',      width: 18 },
            { header: 'Laboratorio',        key: 'laboratorio',       width: 22 },
            { header: 'Nombre Comercial',   key: 'nombre_comercial',  width: 22 },
            { header: 'Concentración',      key: 'concentracion',     width: 16 },
            { header: 'Principio Activo',   key: 'principio_activo',  width: 22 },
            { header: 'Forma Farmacéutica', key: 'forma_farmaceutica',width: 20 },
            { header: 'Titular',            key: 'titular',           width: 20 },
            { header: 'Código de Barras',   key: 'codigo_barras',     width: 18 },
        ];

        const COLS_FINAL = [
            { header: 'Stock Actual',  key: 'stock',          width: 13 },
            { header: 'Stock Mínimo',  key: 'min_stock',      width: 13 },
            { header: 'Precio Compra', key: 'purchase_price', width: 15 },
            { header: 'Precio Venta',  key: 'sale_price',     width: 15 },
            { header: 'Observaciones', key: 'description',    width: 40 },
        ];

        let colsEspeciales = [];
        if (tipoNegocio === 'juguteria') colsEspeciales = COLS_JUGUTERIA;
        else if (tipoNegocio === 'botica') colsEspeciales = COLS_BOTICA;

        const cols = [...COLS_BASE, ...colsEspeciales, ...COLS_FINAL];
        cols.forEach((col, i) => { sheet.getColumn(i + 1).width = col.width; });

        const headerRow = sheet.addRow(cols.map(c => c.header));
        headerRow.height = 24;
        const headerColor = tipoNegocio === 'juguteria' ? 'FF7C3AED' : tipoNegocio === 'botica' ? 'FF065F46' : 'FF2563EB';
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerColor } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Fila de ejemplo
        if (tipoNegocio === 'juguteria') {
            sheet.addRow(['ARM-001', 'Halcón Milenario 3D', 'Star Wars', 'unidad', 1351, 'Experto', 'Madera MDF', 10, 2, 45.00, 85.00, 'Edición coleccionista']);
        } else if (tipoNegocio === 'botica') {
            sheet.addRow(['MED-001', 'Paracetamol 500mg', 'Analgésicos', 'caja', 'Caja x 100 tab', 'Laboratorios Bagó', 'Panadol', '500mg', 'Paracetamol', 'Tableta', 'Bagó S.A.', '7751234567890', 50, 10, 12.00, 25.00, 'Venta con o sin receta']);
        } else {
            sheet.addRow(['PRD-001', 'Producto Ejemplo', 'General', 'unidad', 20, 5, 10.00, 18.00, 'Observación']);
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `plantilla_productos_${tipoNegocio}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        showToast('📥 Plantilla de Excel descargada', 'success');
    };

    // ==================== IMPORTACIÓN DESDE EXCEL ====================

    const handleImportFileChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = ''; // reset input para poder reimportar el mismo archivo
        if (!file) return;

        try {
            const ExcelJS = (await import('exceljs')).default;
            const wb = new ExcelJS.Workbook();
            const buffer = await file.arrayBuffer();
            await wb.xlsx.load(buffer);

            // Buscar la hoja "Productos" o usar la primera disponible
            const sheet = wb.getWorksheet('Productos') || wb.worksheets[0];
            if (!sheet) {
                showToast('❌ El archivo no contiene hojas válidas', 'error');
                return;
            }

            // Leer encabezados (primera fila con contenido real, saltar filas de título)
            let headerRowIndex = null;
            let headers = [];
            sheet.eachRow((row, rowNumber) => {
                if (headerRowIndex !== null) return;
                const vals = row.values.slice(1).map(v => (v != null ? String(v).trim().toLowerCase() : ''));
                // La fila de encabezados contiene "nombre", "código", "armable" o "producto"
                if (vals.some(v => v === 'nombre' || v === 'código' || v === 'codigo' || v.includes('nombre') || v.includes('armable') || v.includes('producto'))) {
                    headerRowIndex = rowNumber;
                    headers = vals;
                }
            });

            if (!headerRowIndex) {
                showToast('❌ No se encontraron encabezados válidos (Código, Nombre…)', 'error');
                return;
            }

            // Mapeo flexible de nombres de columna → campo interno
            const colMap = {
                // Código
                'código': 'code', 'codigo': 'code', 'sku': 'code', 'código interno': 'code', 'codigo interno': 'code',
                // Nombre
                'nombre': 'name', 'nombre del armable': 'name', 'nombre del armable / set': 'name', 'armable': 'name', 'armable / set': 'name', 'producto': 'name', 'nombre del producto': 'name',
                // Categoría
                'categoría': 'category', 'categoria': 'category', 'familia': 'category', 'categoría / familia': 'category',
                // Unidad
                'unidad': 'unit', 'unidad de medida': 'unit', 'unidad de venta': 'unit',
                'estado': null, // solo lectura, ignorar al importar
                // Armable / Jugutería
                'n° piezas': 'numero_piezas', 'n piezas': 'numero_piezas', 'numero piezas': 'numero_piezas', 'número piezas': 'numero_piezas', 'número de piezas': 'numero_piezas', 'numero de piezas': 'numero_piezas', 'piezas': 'numero_piezas', 'nº piezas': 'numero_piezas',
                'nivel dificultad': 'nivel_dificultad', 'nivel de dificultad': 'nivel_dificultad', 'dificultad': 'nivel_dificultad',
                'material': 'material',
                // Farmacéutico / Botica
                'presentación': 'presentacion', 'presentacion': 'presentacion',
                'laboratorio': 'laboratorio',
                'nombre comercial': 'nombre_comercial',
                'concentración': 'concentracion', 'concentracion': 'concentracion',
                'principio activo': 'principio_activo',
                'forma farmacéutica': 'forma_farmaceutica', 'forma farmaceutica': 'forma_farmaceutica',
                'titular': 'titular',
                'código de barras': 'codigo_barras', 'codigo de barras': 'codigo_barras', 'barcode': 'codigo_barras',
                // Inventario
                'stock actual': 'stock', 'stock': 'stock', 'cantidad': 'stock', 'existencias': 'stock',
                'stock mínimo': 'min_stock', 'stock minimo': 'min_stock', 'stock min': 'min_stock', 'min stock': 'min_stock',
                // Precios
                'precio compra': 'purchase_price', 'precio de compra': 'purchase_price', 'p. compra': 'purchase_price', 'p.compra': 'purchase_price', 'costo': 'purchase_price',
                'precio venta': 'sale_price', 'precio de venta': 'sale_price', 'p. venta': 'sale_price', 'p.venta': 'sale_price', 'precio': 'sale_price',
                'ganancia': null, // calculado, ignorar al importar
                // Observaciones
                'observaciones': 'description', 'observacion': 'description', 'descripción': 'description', 'descripcion': 'description', 'notas': 'description', 'detalle': 'description',
            };

            const rows = [];
            sheet.eachRow((row, rowNumber) => {
                if (rowNumber <= headerRowIndex) return;
                const vals = row.values.slice(1);
                const obj = {};
                headers.forEach((h, i) => {
                    const field = colMap[h];
                    if (field) obj[field] = vals[i] != null ? vals[i] : null;
                });
                // Solo incluir filas con nombre
                if (obj.name && String(obj.name).trim() !== '') {
                    rows.push(obj);
                }
            });

            if (rows.length === 0) {
                showToast('⚠️ El archivo no contiene filas de datos', 'error');
                return;
            }

            setImportRows(rows);
            setImportResult(null);
            setShowImportModal(true);
        } catch (err) {
            console.error('Error leyendo Excel:', err);
            showToast('❌ Error al leer el archivo Excel', 'error');
        }
    };

    const handleConfirmImport = async () => {
        if (importRows.length === 0) return;
        setImportLoading(true);
        try {
            const res = await productsAPI.importBulk(importRows, effectiveSucursal);
            setImportResult(res.data?.data || null);
            showToast(res.data?.message || '✅ Importación completada', 'success');
            loadProducts();
            loadStats();
        } catch (err) {
            const msg = err.response?.data?.message || '❌ Error al importar';
            showToast(msg, 'error');
            setImportResult({ creados: 0, omitidos: 0, errores: [{ fila: '-', motivo: msg }] });
        } finally {
            setImportLoading(false);
        }
    };

    // Configuración del Menú ActionModal
    const getProductActions = () => {
        const product = actionProduct;
        if (!product) return [];
        const actions = [];

        if (filter === 'active') {
            if (isBotica()) {
                actions.push({
                    icon: '💊',
                    title: 'Lotes y Vencimientos (FEFO)',
                    subtitle: 'Ver trazabilidad y recepcionar stock',
                    variant: 'stock',
                    onClick: () => handleOpenLotes(product)
                });
            }
            actions.push({
                icon: '👁️', 
                title: (isAdmin() || isEncargada()) ? 'Ver / Editar' : 'Ver Detalles', 
                subtitle: (isAdmin() || isEncargada()) ? 'Modificar datos' : 'Información del producto', 
                variant: 'view',
                onClick: () => handleEdit(product)
            });
            actions.push({
                icon: '📦', title: 'Ajustar Stock (Kardex)', subtitle: `Actual: ${product.stock} un.`, variant: 'stock',
                onClick: () => handleAdjustStock(product)
            });
            // 💰 Precio especial para la sucursal seleccionada (solo Dueño/Admin)
            if (isAdmin() && sucursalActiva !== null) {
                actions.push({
                    icon: '💰',
                    title: 'Precio Especial',
                    subtitle: product.precio_especial ? `Especial: S/. ${parseFloat(product.precio_especial).toFixed(2)}` : 'Definir para esta sucursal',
                    variant: 'stock',
                    onClick: () => {
                        setShowActionModal(false);
                        setPrecioModalProduct({ ...product, sucursal_id_ctx: sucursalActiva, producto_id: product.id });
                        setShowPrecioModal(true);
                    }
                });
            }
            // 🔒 Solo el Dueño/Admin puede mover a la papelera o eliminar
            if (isAdmin()) {
                actions.push({
                    icon: '🗑️', title: 'Mover a Papelera', subtitle: 'Desactivar producto', variant: 'delete',
                    onClick: () => handleDelete(product.id)
                });
            }
        } else {
            // 🔒 Solo el Dueño/Admin puede restaurar o eliminar definitivamente
            if (isAdmin()) {
                actions.push({
                    icon: '♻️', title: 'Restaurar', subtitle: 'Volver a activos', variant: 'stock',
                    onClick: () => handleRestore(product.id)
                });
                actions.push({
                    icon: '❌', title: 'Eliminar Definitivo', subtitle: 'Irreversible', variant: 'delete',
                    onClick: () => handlePermanentDelete(product.id)
                });
            }
        }
        return actions;
    };

    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentData = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => { setCurrentPage(1); }, [filteredProducts.length]);

    // 📱 Early return para móvil — sin fragmentos anidados
    if (isMobile) {
        return (
            <>
                <ProductsMobile
                    products={filteredProducts}
                    stats={stats}
                    isLoading={isLoading}
                    filter={filter}
                    alertasLotes={alertasLotes}
                    sucursales={sucursales}
                    sucursalActiva={sucursalActiva}
                    currentSucursalNombre={!isAdmin() ? currentSucursalNombre : ''}
                    trashedCount={trashedCount}
                    onOpenActions={handleOpenActionModal}
                    onCreate={handleCreate}
                    onRefresh={() => { loadProducts(); loadStats(); if (isBotica()) loadAlertasLotes(); }}
                    onFilterChange={setFilter}
                    onSucursalChange={setSucursalActiva}
                />
                {/* Modal: Formulario producto */}
                {isModalOpen && (
                    <div className="product-modal-overlay">
                        <div className="product-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="product-modal-header">
                                <h2>{selectedProduct ? '✏️ Editar Producto' : '📦 Nuevo Producto'}</h2>
                                <button className="close-btn" onClick={() => setIsModalOpen(false)}>&times;</button>
                            </div>
                            <ProductForm
                                initialData={selectedProduct}
                                onSubmit={handleFormSubmit}
                                onCancel={() => setIsModalOpen(false)}
                                isSaving={isSaving}
                                isBotica={isBotica()}
                                isJuguteria={isJuguteria()}
                            />
                        </div>
                    </div>
                )}
                {/* Modal: Ajuste de stock */}
                {showStockModal && stockProduct && (
                    <InternalModal
                        title={`Ajustar Stock: ${stockProduct.name}`}
                        onClose={() => setShowStockModal(false)}
                    >
                        <StockAdjustmentModal
                            product={stockProduct}
                            onSubmit={handleStockSubmit}
                            onClose={() => setShowStockModal(false)}
                        />
                    </InternalModal>
                )}
                {/* Modal: Lotes y vencimientos */}
                {showLotesModal && lotesProduct && (
                    <LotesModal
                        product={lotesProduct}
                        sucursalId={effectiveSucursal}
                        sucursalNombre={sucursales.find(s => s.id === effectiveSucursal)?.nombre || currentSucursalNombre}
                        onClose={() => setShowLotesModal(false)}
                        onStockUpdated={() => { loadProducts(); loadStats(); loadAlertasLotes(); }}
                        showToast={showToast}
                    />
                )}
                {/* Modal: Acciones */}
                <ActionModal
                    isOpen={showActionModal}
                    onClose={() => setShowActionModal(false)}
                    title={filter === 'active' ? 'Acciones de Producto' : 'Acciones de Papelera'}
                    itemName={actionProduct?.name}
                    itemSubtitle={`Código: ${actionProduct?.code}`}
                    actions={getProductActions()}
                />
                {/* Modal: Precio especial */}
                {showPrecioModal && precioModalProduct && (
                    <PrecioEspecialModal
                        producto={precioModalProduct}
                        sucursalNombre={sucursales.find(s => s.id === sucursalActiva)?.nombre || 'Sucursal'}
                        onClose={() => setShowPrecioModal(false)}
                        onSaved={(msg) => { setShowPrecioModal(false); showToast(msg, 'success'); loadProducts(); }}
                        onError={(err) => showToast(err, 'error')}
                    />
                )}
                {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            </>
        );
    }


    // 🖥️ Vista escritorio
    return (
        <div className="products-page" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        {filter === 'active' ? 'Gestión de Productos' : 'Papelera de Productos'}
                    </h1>
                    <p className="page-subtitle">
                        {filter === 'active'
                            ? (!isAdmin() && currentSucursalNombre ? `Inventario de ${currentSucursalNombre}` : 'Administración de inventario')
                            : 'Productos eliminados'}
                    </p>
                </div>
            </div>

            {/* Selector de sucursales: EXCLUSIVO para Dueño/Admin */}
            {filter === 'active' && isAdmin() && (
                <div className="suc-selector-chips" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
                    <button
                        onClick={() => setSucursalActiva(null)}
                        style={{
                            padding: '8px 14px', borderRadius: 100, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            border: sucursalActiva === null ? '1px solid rgba(99,102,241,0.6)' : '1px solid rgba(255,255,255,0.1)',
                            background: sucursalActiva === null ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.04)',
                            color: sucursalActiva === null ? '#a5b4fc' : '#94a3b8',
                        }}
                    >
                        🌐 Todas las sucursales
                    </button>
                    {sucursales.map(s => (
                        <button
                            key={s.id}
                            onClick={() => setSucursalActiva(s.id)}
                            style={{
                                padding: '8px 14px', borderRadius: 100, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                border: sucursalActiva === s.id ? '1px solid rgba(245,158,11,0.6)' : '1px solid rgba(255,255,255,0.1)',
                                background: sucursalActiva === s.id ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.04)',
                                color: sucursalActiva === s.id ? '#f59e0b' : '#94a3b8',
                            }}
                        >
                            {s.es_offline ? '📴' : '🏪'} {s.nombre}
                        </button>
                    ))}
                </div>
            )}

            {/* KPIs */}
            {/* Semáforo de Vencimientos Farmacéuticos (Exclusivo Boticas) */}
            {filter === 'active' && isBotica() && alertasLotes && alertasLotes.resumen && alertasLotes.resumen.total_alertas > 0 && (
                <div className="farmacia-semaforo-bar">
                    <div className="semaforo-bar-info">
                        <span style={{ fontSize: '1.5rem' }}>🚦</span>
                        <div>
                            <h3>Semáforo de Vencimientos (Próximos 90 días)</h3>
                            <p>Trazabilidad FEFO obligatoria para dispensación en boticas y farmacias</p>
                        </div>
                    </div>
                    <div className="semaforo-pills">
                        {alertasLotes.resumen.vencidos > 0 && (
                            <span 
                                className="semaforo-pill vencidos" 
                                title="Click para filtrar lotes vencidos"
                                style={{ cursor: 'pointer', opacity: (stockFilter === 'vencidos' || !['vencidos','criticos','por_vencer'].includes(stockFilter)) ? 1 : 0.4 }}
                                onClick={() => setStockFilter(stockFilter === 'vencidos' ? 'all' : 'vencidos')}
                            >
                                ⚫ Vencidos: <span className="pill-count">{alertasLotes.resumen.vencidos}</span>
                            </span>
                        )}
                        {alertasLotes.resumen.criticos > 0 && (
                            <span 
                                className="semaforo-pill criticos" 
                                title="Click para filtrar lotes con menos de 30 días de vigencia"
                                style={{ cursor: 'pointer', opacity: (stockFilter === 'criticos' || !['vencidos','criticos','por_vencer'].includes(stockFilter)) ? 1 : 0.4 }}
                                onClick={() => setStockFilter(stockFilter === 'criticos' ? 'all' : 'criticos')}
                            >
                                🔴 Críticos (&le; 30d): <span className="pill-count">{alertasLotes.resumen.criticos}</span>
                            </span>
                        )}
                        {alertasLotes.resumen.por_vencer > 0 && (
                            <span 
                                className="semaforo-pill por-vencer" 
                                title="Click para filtrar lotes por vencer entre 31 y 90 días"
                                style={{ cursor: 'pointer', opacity: (stockFilter === 'por_vencer' || !['vencidos','criticos','por_vencer'].includes(stockFilter)) ? 1 : 0.4 }}
                                onClick={() => setStockFilter(stockFilter === 'por_vencer' ? 'all' : 'por_vencer')}
                            >
                                🟡 Por Vencer (31-90d): <span className="pill-count">{alertasLotes.resumen.por_vencer}</span>
                            </span>
                        )}
                    </div>
                </div>
            )}

            {filter === 'active' && stats && (
                <div className="kpi-grid">
                    <div className="kpi-card kpi-total"><div className="kpi-label">Total</div><div className="kpi-value">{stats.active}</div></div>
                    <div className="kpi-card kpi-low-stock"><div className="kpi-label">Stock Bajo</div><div className="kpi-value">{stats.low_stock}</div></div>
                    <div className="kpi-card kpi-depleted"><div className="kpi-label">Agotados</div><div className="kpi-value">{stats.out_of_stock}</div></div>
                    <div className="kpi-card kpi-categories"><div className="kpi-label">Categorías</div><div className="kpi-value">{stats.total_categories}</div></div>
                </div>
            )}

            {/* Toolbar */}
            <div className="toolbar">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Buscar producto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')}
                    className={`btn-stock-filter ${stockFilter === 'low' ? 'active' : ''}`}
                    style={{ background: stockFilter === 'low' ? '#f59e0b' : '#374151', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                >
                    🟡 Stock Bajo
                </button>
                <button
                    onClick={() => setStockFilter(stockFilter === 'out' ? 'all' : 'out')}
                    className={`btn-stock-filter ${stockFilter === 'out' ? 'active' : ''}`}
                    style={{ background: stockFilter === 'out' ? '#ef4444' : '#374151', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                >
                    🔴 Agotados
                </button>

                <div className="toolbar-actions">
                    {filter === 'active' ? (
                        <>
                            <button
                                onClick={() => {
                                    loadProducts();
                                    loadStats();
                                    if (isBotica()) loadAlertasLotes();
                                    showToast("🔄 Datos actualizados", "success");
                                }}
                                className="btn btn-secondary"
                                title="Actualizar lista de productos y estadísticas"
                            >
                                🔄 Actualizar
                            </button>
                            <button
                                onClick={() => setShowQuickEdit(true)}
                                className="btn btn-secondary"
                                title="Edición rápida de producto (F2)"
                                style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', border: 'none', fontWeight: 700 }}
                            >
                                ⚡ Edición Rápida <kbd style={{ background:'rgba(255,255,255,0.2)', borderRadius:'4px', padding:'1px 5px', fontSize:'0.75rem' }}>F2</kbd>
                            </button>
                            <button
                                onClick={() => setShowSearchModal(true)}
                                className="btn btn-secondary"
                                title="Búsqueda avanzada por nombre, código, laboratorio o principio activo"
                            >
                                🔍 Búsqueda Avanzada
                            </button>

                            <button onClick={() => exportToExcel().catch(err => console.error(err))} className="btn btn-secondary">📗 Exportar Excel</button>
                            {(isAdmin() || isEncargada()) && (
                                <>
                                    <button
                                        onClick={() => document.getElementById('import-excel-input').click()}
                                        className="btn btn-secondary"
                                        style={{ background: 'linear-gradient(135deg,#059669,#047857)', color: 'white', border: 'none', fontWeight: 700 }}
                                        title="Importar productos desde un archivo Excel (.xlsx)"
                                    >
                                        📤 Importar Excel
                                    </button>
                                    <input
                                        id="import-excel-input"
                                        type="file"
                                        accept=".xlsx,.xls"
                                        style={{ display: 'none' }}
                                        onChange={handleImportFileChange}
                                    />
                                </>
                            )}
                            {isAdmin() && (
                                <button onClick={() => setFilter('inactive')} className="btn btn-secondary btn-with-badge">
                                    Papelera {trashedCount > 0 && <span className="badge">{trashedCount}</span>}
                                </button>
                            )}
                            {(isAdmin() || isEncargada()) && (
                                <button onClick={handleCreate} className="btn btn-primary">
                                    {isJuguteria() ? '🧩 Nuevo Armable' : isBotica() ? '💊 Nuevo Medicamento' : 'Nuevo Producto'}
                                </button>
                            )}
                        </>
                    ) : (
                        <button onClick={() => setFilter('active')} className="btn btn-secondary">Volver</button>
                    )}
                </div>
            </div>

            {filteredProducts.length > 0 && (
                <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px', marginBottom: '15px', flexWrap: 'wrap' }}>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="btn-page-nav">← Anterior</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                        .reduce((acc, p, i, arr) => {
                            if (i > 0 && arr[i-1] !== p - 1) acc.push('...');
                            acc.push(p);
                            return acc;
                        }, [])
                        .map((p, i) => p === '...' 
                            ? <span key={i} style={{ padding: '0 4px' }}>...</span>
                            : <button key={p} onClick={() => setCurrentPage(p)} className={`btn-page-nav ${currentPage === p ? 'active' : ''}`} style={{ fontWeight: currentPage === p ? '700' : 'normal', background: currentPage === p ? '#3b82f6' : '', color: currentPage === p ? 'white' : '' }}>{p}</button>
                        )
                    }
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="btn-page-nav">Siguiente →</button>
                    <span style={{ marginLeft: '10px', fontSize: '13px', color: '#64748b' }}>
                        {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredProducts.length)} de {filteredProducts.length}
                    </span>
                    <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ marginLeft: '8px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <input
                        type="number"
                        placeholder="..."
                        min="1"
                        max="500"
                        style={{ width: '55px', padding: '4px 6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', marginLeft: '4px' }}
                        onBlur={(e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 1 && val <= 500) { setItemsPerPage(val); setCurrentPage(1); }
                            e.target.value = '';
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                const val = parseInt(e.target.value);
                                if (val >= 1 && val <= 500) { setItemsPerPage(val); setCurrentPage(1); }
                                e.target.value = '';
                            }
                        }}
                    />
                </div>
            )}

            {/* TABLA DE PRODUCTOS */}
            {isLoading ? (
                <div className="loading-container"><div className="loading-spinner"></div><p>Cargando...</p></div>
            ) : filteredProducts.length === 0 ? (
                <div className="empty-container"><p>No hay productos</p></div>
            ) : (
                <ProductTable
                    products={currentData}
                    onOpenActions={handleOpenActionModal}
                    onOpenLotes={handleOpenLotes}
                    isBotica={isBotica()}
                    isJuguteria={isJuguteria()}
                    viewMode={filter}
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={(field, dir) => {
                        setSortField(field);
                        setSortDir(dir !== undefined ? dir : (sortField === field ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'));
                        setCurrentPage(1);
                    }}
                    categoryFilter={categoryFilter}
                    onCategoryFilter={(v) => { setCategoryFilter(v); setCurrentPage(1); }}
                    uniqueCategories={uniqueCategories}
                    unitFilter={unitFilter}
                    onUnitFilter={(v) => { setUnitFilter(v); setCurrentPage(1); }}
                    uniqueUnits={uniqueUnits}
                    statusFilter={statusFilter}
                    onStatusFilter={(v) => { setStatusFilter(v); setCurrentPage(1); }}
                    onClearFilters={() => {
                        setSortField(''); setSortDir('asc');
                        setCategoryFilter(''); setUnitFilter(''); setStatusFilter('');
                        setCurrentPage(1);
                    }}
                />
            )}


            {/* ACTION MODAL */}
            <ActionModal
                isOpen={showActionModal}
                onClose={() => setShowActionModal(false)}
                title={filter === 'active' ? "Acciones de Producto" : "Acciones de Papelera"}
                itemName={actionProduct?.name}
                itemSubtitle={`Código: ${actionProduct?.code}`}
                actions={getProductActions()}
            />

            {/* MODAL PRECIO ESPECIAL */}
            {showPrecioModal && precioModalProduct && (
                <PrecioEspecialModal
                    producto={precioModalProduct}
                    sucursalNombre={sucursales.find(s => s.id === sucursalActiva)?.nombre || 'Sucursal'}
                    onClose={() => setShowPrecioModal(false)}
                    onSaved={(msg) => {
                        setShowPrecioModal(false);
                        showToast(msg, 'success');
                        loadProducts();
                    }}
                    onError={(err) => showToast(err, 'error')}
                />
            )}

            {/* MODAL FORMULARIO */}
            {isModalOpen && (
                <div className="product-modal-overlay">
                    <div className="product-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="product-modal-header">
                            <h2>{selectedProduct ? (isJuguteria() ? '✏️ Editar Armable' : isBotica() ? '✏️ Editar Medicamento' : '✏️ Editar Producto') : (isJuguteria() ? '🧩 Nuevo Armable' : isBotica() ? '💊 Nuevo Medicamento' : '📦 Nuevo Producto')}</h2>
                            <button className="close-btn" onClick={() => setIsModalOpen(false)}>&times;</button>
                        </div>
                        <ProductForm
                            initialData={selectedProduct}
                            onSubmit={handleFormSubmit}
                            onCancel={() => setIsModalOpen(false)}
                            isSaving={isSaving}
                            isBotica={isBotica()}
                            isJuguteria={isJuguteria()}
                        />
                    </div>
                </div>
            )}

            {/* MODAL STOCK */}
            {showStockModal && stockProduct && (
                <InternalModal
                    title={`Ajustar Stock: ${stockProduct.name}`}
                    onClose={() => setShowStockModal(false)}
                >
                    <StockAdjustmentModal
                        product={stockProduct}
                        onSubmit={handleStockSubmit}
                        onClose={() => setShowStockModal(false)}
                    />
                </InternalModal>
            )}

            {/* MODAL LOTES Y VENCIMIENTOS */}
            {showLotesModal && lotesProduct && (
                <LotesModal
                    product={lotesProduct}
                    sucursalId={effectiveSucursal}
                    sucursalNombre={sucursales.find(s => s.id === effectiveSucursal)?.nombre || currentSucursalNombre}
                    onClose={() => setShowLotesModal(false)}
                    onStockUpdated={() => {
                        loadProducts();
                        loadStats();
                        loadAlertasLotes();
                    }}
                    showToast={showToast}
                />
            )}

            {/* TOAST */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* MODAL BÚSQUEDA AVANZADA */}
            <ProductSearchModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
                sucursalId={effectiveSucursal}
            />

            {/* MODAL EDICIÓN RÁPIDA (F2) */}
            <QuickEditModal
                isOpen={showQuickEdit}
                onClose={() => setShowQuickEdit(false)}
                sucursalId={effectiveSucursal}
                onSaved={() => {
                    loadProducts();
                    loadStats();
                    showToast('✅ Producto actualizado desde edición rápida', 'success');
                }}
            />

            {/* MODAL IMPORTACIÓN DESDE EXCEL */}
            {showImportModal && (
                <div className="product-modal-overlay" onClick={() => { if (!importLoading) { setShowImportModal(false); setImportResult(null); } }}>
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#1e293b', color: '#e2e8f0', borderRadius: 16,
                            width: '92vw', maxWidth: 900, maxHeight: '85vh',
                            display: 'flex', flexDirection: 'column',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.6)', overflow: 'hidden'
                        }}
                    >
                        {/* Header */}
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>
                                    📤 Importar {isJuguteria() ? 'Armables / Sets' : isBotica() ? 'Medicamentos' : 'Productos'} desde Excel
                                </h2>
                                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                                    {importResult
                                        ? 'Resultado de la importación'
                                        : `Se encontraron ${importRows.length} producto(s) en el archivo. Revisa la vista previa antes de confirmar.`}
                                </p>
                                {!importResult && (
                                    <button
                                        onClick={downloadTemplate}
                                        style={{
                                            background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
                                            color: '#93c5fd', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem',
                                            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8
                                        }}
                                        title="Descarga un archivo Excel con las columnas exactas listas para llenar"
                                    >
                                        📄 Descargar plantilla para {isJuguteria() ? '🧩 Armables' : isBotica() ? '💊 Farmacia' : '📦 Tienda'}
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => { setShowImportModal(false); setImportResult(null); }}
                                disabled={importLoading}
                                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}
                            >×</button>
                        </div>

                        {/* Body */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                            {importResult ? (
                                /* RESULTADO */
                                <div>
                                    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, minWidth: 120, background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
                                            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#10b981' }}>{importResult.creados}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#6ee7b7', marginTop: 4 }}>Creados</div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 120, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
                                            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#f59e0b' }}>{importResult.omitidos}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#fcd34d', marginTop: 4 }}>Omitidos (duplicados)</div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 120, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
                                            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#ef4444' }}>{importResult.errores?.length - importResult.omitidos || 0}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#fca5a5', marginTop: 4 }}>Errores</div>
                                        </div>
                                    </div>
                                    {importResult.errores?.length > 0 && (
                                        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 12 }}>
                                            <p style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600, color: '#f59e0b' }}>⚠️ Detalle de problemas:</p>
                                            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                                                {importResult.errores.map((e, i) => (
                                                    <div key={i} style={{ fontSize: '0.8rem', color: '#94a3b8', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                                        <span style={{ color: '#f87171' }}>Fila {e.fila}{e.nombre ? ` — ${e.nombre}` : ''}: </span>{e.motivo}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* VISTA PREVIA */
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                                        <thead>
                                            <tr style={{ background: 'rgba(99,102,241,0.2)' }}>
                                                {isJuguteria() ? (
                                                    ['#', 'Código', 'Nombre / Set', 'Categoría', 'N° Piezas', 'Dificultad', 'Material', 'Stock', 'P. Compra', 'P. Venta'].map(h => (
                                                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#a5b4fc', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                                    ))
                                                ) : isBotica() ? (
                                                    ['#', 'Código', 'Nombre', 'Presentación', 'Laboratorio', 'Categoría', 'Unidad', 'Stock', 'P. Venta'].map(h => (
                                                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#a5b4fc', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                                    ))
                                                ) : (
                                                    ['#', 'Código', 'Nombre', 'Categoría', 'Unidad', 'Stock', 'Stock Mín.', 'P. Compra', 'P. Venta'].map(h => (
                                                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#a5b4fc', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                                    ))
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importRows.slice(0, 100).map((r, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                                    <td style={{ padding: '6px 10px', color: '#475569' }}>{i + 1}</td>
                                                    <td style={{ padding: '6px 10px', color: '#64748b' }}>{r.code || '—'}</td>
                                                    <td style={{ padding: '6px 10px', fontWeight: 500 }}>{r.name}</td>
                                                    {isJuguteria() ? (
                                                        <>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.category || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#c084fc', fontWeight: 600 }}>{r.numero_piezas || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.nivel_dificultad || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.material || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#10b981', fontWeight: 600 }}>{r.stock ?? 0}</td>
                                                            <td style={{ padding: '6px 10px', color: '#f59e0b' }}>{r.purchase_price != null && r.purchase_price !== '' ? `S/. ${parseFloat(r.purchase_price).toFixed(2)}` : '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#38bdf8', fontWeight: 600 }}>{r.sale_price != null && r.sale_price !== '' ? `S/. ${parseFloat(r.sale_price).toFixed(2)}` : '—'}</td>
                                                        </>
                                                    ) : isBotica() ? (
                                                        <>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.presentacion || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.laboratorio || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.category || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.unit || 'unidad'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#10b981', fontWeight: 600 }}>{r.stock ?? 0}</td>
                                                            <td style={{ padding: '6px 10px', color: '#38bdf8', fontWeight: 600 }}>{r.sale_price != null && r.sale_price !== '' ? `S/. ${parseFloat(r.sale_price).toFixed(2)}` : '—'}</td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.category || '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.unit || 'unidad'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#10b981', fontWeight: 600 }}>{r.stock ?? 0}</td>
                                                            <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{r.min_stock ?? 0}</td>
                                                            <td style={{ padding: '6px 10px', color: '#f59e0b' }}>{r.purchase_price != null && r.purchase_price !== '' ? `S/. ${parseFloat(r.purchase_price).toFixed(2)}` : '—'}</td>
                                                            <td style={{ padding: '6px 10px', color: '#38bdf8', fontWeight: 600 }}>{r.sale_price != null && r.sale_price !== '' ? `S/. ${parseFloat(r.sale_price).toFixed(2)}` : '—'}</td>
                                                        </>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {importRows.length > 100 && (
                                        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem', marginTop: 8 }}>
                                            Mostrando 100 de {importRows.length} filas en la vista previa.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button
                                onClick={() => { setShowImportModal(false); setImportResult(null); }}
                                disabled={importLoading}
                                style={{ padding: '9px 20px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', cursor: 'pointer', fontWeight: 600 }}
                            >
                                {importResult ? 'Cerrar' : 'Cancelar'}
                            </button>
                            {!importResult && (
                                <button
                                    onClick={handleConfirmImport}
                                    disabled={importLoading}
                                    style={{ padding: '9px 24px', borderRadius: 8, background: importLoading ? '#374151' : 'linear-gradient(135deg,#059669,#047857)', border: 'none', color: 'white', cursor: importLoading ? 'not-allowed' : 'pointer', fontWeight: 700, minWidth: 160 }}
                                >
                                    {importLoading ? '⏳ Importando...' : `✅ Confirmar (${importRows.length} productos)`}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ProductsPage;